import { HttpResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { I18nService } from '@app/i18n/services';
import { CompanyService } from '@services/company';
import { DialogClass, DialogService } from '@services/dialog';
import { FeedbackService, FeedbackStyle, FeedbackTitle } from '@services/feedback';
import { IAppTerm, ISortedAppTerm, TermState, TermType } from '@services/legals/legals.models';
import { LegalsService } from '@services/legals/legals.service';
import { ProgressIndicatorService } from '@services/progress';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { ILocale } from '../../../../../../services/utils/utils.models';

import { UtilsService } from '../../../../../../services/utils/utils.service';
import { LegalAppUpdateDialogComponent } from '../legal-app-update-dialog';
import { LegalAppKeyAssociation, LegalAppKeyAssociationDialogComponent } from '../legal-appkey-association-dialog';
import { LegalNewVersionDialogComponent } from '../legal-new-version-dialog';
import { EditAppLegal } from './legal-app-edit-dialog.models';

@Component({
	selector: 'app-legal-app-edit-dialog',
	templateUrl: './legal-app-edit-dialog.dialog.html'
})
export class LegalAppEditDialogComponent implements OnInit {
	public versionSelector = this.formBuilder.control({});
	public localisationSelector = this.formBuilder.control('');
	public versions = [];
	public localisations: { name: string; code: string }[] = [];
	public locales: ILocale[] = [];
	public isAdmin = false;
	public termStates = TermState;

	constructor(
		private legalsService: LegalsService,
		private companyService: CompanyService,
		private i18nService: I18nService,
		private dialogService: DialogService,
		private progressIndicatorService: ProgressIndicatorService,
		private feedbackService: FeedbackService,
		@Inject(MAT_DIALOG_DATA) public dialogData: EditAppLegal,
		private dialogRef: MatDialogRef<LegalAppEditDialogComponent>,
		private formBuilder: FormBuilder,
		private utilsService: UtilsService
	) {}

	public ngOnInit(): void {
		this.isAdmin = this.companyService.isAdminContact();

		this.init();
	}

	private init(selectedVersion = this.versionSelector.value?.key): void {
		this.localisationSelector.setValue('');

		if (this.dialogData.terms?.displayNames) this.getLocalisations();

		this.versions = Object.values(this.dialogData.terms.documents)
			.map((document) => {
				const state = document.status === 'active' ? TermState.live : (document.status as TermState);

				return {
					badge: this.legalsService.getStateBadge(state),
					stateLabel: this.legalsService.getStateLabel(state),
					state: state,
					key: document.version,
					label: document.displayVersion
				};
			})
			.sort((a, b) => b.key - a.key);

		const versionIndex = this.versions.findIndex((version) => selectedVersion === version.key);

		if (versionIndex > -1) {
			this.versionSelector.setValue(this.versions[versionIndex]);
		} else {
			this.versionSelector.setValue(this.versions[0]);
		}
	}

	public confirmPublishTerm(version: string): void {
		this.feedbackService.confirm({
			title: FeedbackTitle.attention,
			message: this.i18nService.get('Publish?', 'app.prompt.publish-document'),
			style: FeedbackStyle.error,
			callback: (confirmed: boolean) => {
				if (confirmed) {
					this.publishTerm(version);
				}
			}
		});
	}

	public viewTerms(version: string): void {
		if (this.dialogData.terms.documents[version]?.filename) {
			this.progressIndicatorService.spinner(true);

			this.legalsService
				.getTermData(this.dialogData.terms.termsId, version, TermType.app)
				.pipe(finalize(() => this.progressIndicatorService.spinner(false)))
				.subscribe((res: HttpResponse<string>) => {
					this.legalsService.serveDocument(
						this.legalsService.getAppTermDocument(this.dialogData.terms, version),
						res,
						this.dialogData.editable,
						TermType.app,
						this.getState(this.dialogData.terms.documents[version]),
						() => {}
					);
				});
		} else if (this.dialogData.terms.documents[version]?.url) {
			this.legalsService.serveExternalDocument(this.dialogData.terms.documents[version].url);
		}
	}

	public uploadTerm(): void {
		this.dialogService.open(LegalNewVersionDialogComponent, {
			data: {
				company: this.dialogData.company,
				terms: this.dialogData.terms,
				type: this.dialogData.type,
				callback: (res: ISortedAppTerm) => {
					if (res) {
						this.dialogData.terms = this.legalsService.data.app.find(
							(term) => term.termsId === this.dialogData.terms.termsId
						);

						this.init(this.dialogData.terms.latestVersion);
						this.dialogData.callback(false);
					}
				}
			}
		});
	}

	public updateTerm(): void {
		this.dialogService.open(LegalAppUpdateDialogComponent, {
			data: {
				company: this.dialogData.company,
				terms: this.dialogData.terms,
				type: this.dialogData.type,
				callback: (refreshTerms: boolean) => {
					this.dialogData.terms = this.legalsService.data.app.find(
						(term) => term.termsId === this.dialogData.terms.termsId
					);

					this.init();
					this.dialogData.callback(refreshTerms);
				}
			}
		});
	}

	public confirmDeleteTerm(version?: string): void {
		this.feedbackService.confirm({
			title: FeedbackTitle.attention,
			message: this.i18nService.get('Are you sure you want to delete this document?', 'app.prompt.delete-document'),
			style: FeedbackStyle.error,
			callback: (confirmed: boolean) => {
				if (confirmed) {
					this.deleteTerm(version);
				}
			}
		});
	}

	public viewAppKeyAssociation(): void {
		const data: LegalAppKeyAssociation = {
			company: this.dialogData.company,
			terms: this.dialogData.terms,
			editable: this.dialogData.editable,
			callback: () => {}
		};
		this.dialogService.open(LegalAppKeyAssociationDialogComponent, {
			panelClass: DialogClass.large,
			data
		});
	}

	private getState(term: IAppTerm): TermState {
		return term.status === 'active' ? TermState.live : (term.status as TermState);
	}

	private publishTerm(version: string): void {
		this.progressIndicatorService.spinner(true);

		this.legalsService
			.activeState(this.dialogData.terms.termsId, version, true, this.dialogData.type)
			.subscribe(
				() => {
					this.dialogData.terms = this.legalsService.data.app.find(
						(term) => term.termsId === this.dialogData.terms.termsId
					);

					this.init();
					this.progressIndicatorService.spinner(false);
					this.dialogData.callback(false);
				},
				(error: any) => {
					this.feedbackService.apiError(error);
					this.progressIndicatorService.spinner(false);
				}
			);
	}

	private deleteTerm(version?: string): void {
		this.progressIndicatorService.spinner(true);

		const requests = {};

		if (version) {
			requests[version] = this.legalsService.deleteDraftTerms(this.dialogData.terms.termsId, version);
		} else {
			Object.keys(this.dialogData.terms.documents).forEach((key) => {
				requests[key] = this.legalsService.deleteDraftTerms(this.dialogData.terms.termsId, key);
			});
		}

		forkJoin(requests).subscribe(
			() => {
				const index = this.legalsService.data['app'].findIndex(
					(item) => item.termsId === this.dialogData.terms.termsId
				);

				if (index === -1) {
					this.dialogRef.close();
				} else {
					this.dialogData.terms = this.legalsService.data.app[index];
					this.init();
				}

				this.dialogData.callback(false);
				this.progressIndicatorService.spinner(false);
			},
			(error: any) => {
				this.feedbackService.apiError(error);
				this.progressIndicatorService.spinner(false);
			}
		);
	}

	private getLocalisations(): void {
		this.utilsService.getLocales().subscribe({
			next: (res) => {
				this.localisations = Object.keys(this.dialogData.terms?.displayNames ? this.dialogData.terms.displayNames : {}).map((l) => {
					const locale = res.find((r) => r.code.toLowerCase() === l.toLowerCase());
					const code = locale?.code ? locale.code : l;

					return {
						name: locale?.name ? `${locale.name} (${code})` : `(${code})`,
						code: code
					};
				});
			}
		});
	}
}
